import { Link, useParams, useNavigate } from 'react-router-dom';
import type { StatCardModel } from '@/types/stats';
import type { Project } from '@/types/entities';
import { PageHeader } from '@/components/composites/PageHeader';
import { EmployeeSwitcher } from '@/components/composites/EmployeeSwitcher';
import { StatCardGrid } from '@/components/composites/StatCardGrid';
import { DataTable } from '@/components/composites/DataTable';
import { EmptyState } from '@/components/primitives/EmptyState';
import { Badge } from '@/components/primitives/Badge';
import { useEmployeesWithStats } from '@/hooks/useEmployees';
import { useClients } from '@/hooks/useClients';
import styles from './EmployeeClientDetailPage.module.css';

interface ProjectRow {
  project: Project;
  totalHours: number;
  billableHours: number;
  events: number;
}

export function EmployeeClientDetailPage() {
  const { employeeId, clientId } = useParams<{ employeeId: string; clientId: string }>();
  const navigate = useNavigate();
  const { data: employeesWithStats } = useEmployeesWithStats();
  const { data: clients } = useClients();
  const employee = employeesWithStats.find((e) => e.id === employeeId);
  const client = clients.find((c) => c.id === clientId);

  if (!employee || !client) {
    return (
      <>
        <PageHeader title="Not found" />
        <EmptyState
          title="Employee or client not found"
          description="This combination does not exist in the mockup tenant."
        />
      </>
    );
  }

  const projectCount = Math.max(1, client.projects.length);
  const totalHours = +(employee.totalHours / 3).toFixed(1);
  const billableHours = +(employee.billableHours / 3).toFixed(1);

  const rows: ProjectRow[] = client.projects.map((p, i) => {
    const share = 1 / projectCount;
    const adjust = i === 0 ? 1.2 : 1;
    const rt = +(totalHours * share * adjust).toFixed(1);
    const rb = +(billableHours * share * adjust).toFixed(1);
    return {
      project: p,
      totalHours: rt,
      billableHours: rb,
      events: Math.round((employee.events / 3) * share * adjust),
    };
  });

  const cards: readonly [StatCardModel, StatCardModel, StatCardModel, StatCardModel] = [
    { label: 'Total Hours', value: `${totalHours.toFixed(1)}h` },
    { label: 'Billable Hours', value: `${billableHours.toFixed(1)}h` },
    { label: 'Projects', value: client.projects.length },
    { label: 'Events', value: Math.round(employee.events / 3).toLocaleString() },
  ];

  type Col = 'project' | 'type' | 'totalHours' | 'billableHours' | 'events' | 'actions';

  const columns: Array<{
    key: Col;
    header: string;
    align?: 'left' | 'right';
    render: (row: ProjectRow) => React.ReactNode;
  }> = [
    {
      key: 'project',
      header: 'Project',
      render: (row) => <span className={styles.projectCell}>{row.project.name}</span>,
    },
    {
      key: 'type',
      header: 'Type',
      render: (row) => <Badge variant={row.project.type}>{row.project.type}</Badge>,
    },
    {
      key: 'totalHours',
      header: 'Total Hours',
      align: 'right',
      render: (row) => <span className={styles.hoursCell}>{row.totalHours.toFixed(1)}h</span>,
    },
    {
      key: 'billableHours',
      header: 'Billable Hours',
      align: 'right',
      render: (row) => <span className={styles.hoursCell}>{row.billableHours.toFixed(1)}h</span>,
    },
    {
      key: 'events',
      header: 'Events',
      align: 'right',
      render: (row) => <span className={styles.hoursCell}>{row.events.toLocaleString()}</span>,
    },
    {
      key: 'actions',
      header: 'Actions',
      align: 'right',
      render: () => (
        <Link
          to={`/employees/${employee.id}/clients/${client.id}/projects`}
          className={styles.actionLink}
        >
          View
        </Link>
      ),
    },
  ];

  return (
    <>
      <PageHeader
        title={client.name}
        subtitle={`${employee.name} · ${client.projects.length} projects`}
        actions={
          <EmployeeSwitcher
            currentEmployeeId={employeeId ?? ''}
            onSwitch={(id) => navigate(`/employees/${id}/clients/${clientId}`)}
          />
        }
      />
      <StatCardGrid cards={cards} />
      <DataTable<ProjectRow, Col>
        caption={`Projects for ${employee.name} at ${client.name}`}
        columns={columns}
        rows={rows}
        getRowKey={(row) => row.project.id}
      />
    </>
  );
}
